/**
 * SliderRow — "Size", "Darken", "Space from the bottom": one number, one
 * track, one mono readout.
 *
 * Why a slider and not the inspector's NumField: the essentials view is for
 * someone who does not know that 230 px is a speaker name and 130 px is a
 * caption. A track with a floor and a ceiling makes the whole safe range
 * visible and reachable with one drag; the exact number still shows, so the
 * precision the inspector promises is never hidden — it moves to a readout.
 *
 * Timing contract (the inspector's, exactly): the browser's `input` event is
 * a live PREVIEW (no journal entry, the preview repaints under the thumb),
 * the browser's `change` event is the COMMIT (one entry when the drag ends
 * or the arrow key settles). React maps `onChange` onto `input`, so the real
 * `change` is subscribed natively on the element — that is the only way to
 * get "one journal entry per drag" instead of one per pixel.
 */
import { useEffect, useLayoutEffect, useRef, useState } from "react";

import type { EditPatch } from "../../../../../src/lib/clipEditState.ts";
import { fieldLabelStyle } from "../../styles.ts";
import type { EssentialRowProps } from "./types.ts";

/** Decimals implied by the step — 0.05 → 2, 5 → 0. Keeps the readout from
 *  showing `0.30000000000000004` and from padding an integer with ".00". */
function decimalsFor(step: number): number {
  const s = String(step);
  const dot = s.indexOf(".");
  return dot === -1 ? 0 : s.length - dot - 1;
}

export function formatSliderValue(v: number, step: number, unit?: string): string {
  return `${v.toFixed(decimalsFor(step))}${unit ?? ""}`;
}

export function SliderRow(
  p: {
    label: string;
    value: number;
    min: number;
    max: number;
    step?: number;
    unit?: string;
    toPatch: (v: number) => EditPatch;
    commitLabel: string;
  } & EssentialRowProps,
): React.JSX.Element {
  const { label, value, min, max, step = 1, unit, toPatch, commitLabel, onPreview, onCommit } = p;
  const ref = useRef<HTMLInputElement | null>(null);
  /** Non-null only WHILE dragging. The committed value flows back from the
   *  host through `value`; the draft covers the frames before it does, so the
   *  thumb never stutters against a one-render-late prop. */
  const [draft, setDraft] = useState<number | null>(null);

  // Latest handlers in a ref: the native subscription is set up once, but it
  // must always call the CURRENT toPatch/onCommit (both are inline arrows
  // that close over this render's layout values).
  const live = useRef({ toPatch, onCommit, commitLabel, step, unit });
  useLayoutEffect(() => {
    live.current = { toPatch, onCommit, commitLabel, step, unit };
  });

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const onSettled = (): void => {
      const v = Number(el.value);
      setDraft(null);
      const h = live.current;
      h.onCommit(h.toPatch(v), `${h.commitLabel} → ${formatSliderValue(v, h.step, h.unit)}`);
    };
    el.addEventListener("change", onSettled);
    return () => el.removeEventListener("change", onSettled);
  }, []);

  const shown = draft ?? value;
  return (
    <div style={{ display: "grid", gap: 6, minWidth: 0 }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 8, minWidth: 0 }}>
        <span style={{ ...fieldLabelStyle, marginBottom: 0, minWidth: 0 }}>{label}</span>
        <span
          style={{
            marginLeft: "auto",
            fontFamily: "var(--ql-font-mono)",
            fontSize: 11.5,
            color: "var(--ql-fg)",
            whiteSpace: "nowrap",
          }}
        >
          {formatSliderValue(shown, step, unit)}
        </span>
      </div>
      <input
        ref={ref}
        type="range"
        min={min}
        max={max}
        step={step}
        value={shown}
        // React's onChange IS the native `input` event → live preview only.
        onChange={(e) => {
          const v = Number(e.target.value);
          setDraft(v);
          onPreview(toPatch(v));
        }}
        aria-label={label}
        style={{ width: "100%", accentColor: "var(--ql-yellow)", cursor: "pointer" }}
      />
    </div>
  );
}
